import { get, keyBy, uniq, groupBy } from 'lodash';
import {
  financeService,
  MS_PHASE_3_ENDPOINTS,
} from 'utils/msEndpointConstants';
import { getCentreByIds, getProgramByIds } from 'services/CentreService';
import {
  getApprovedFeeDraftByFeeTierIds,
  getFeeByFeeTierIds,
  getFeeDraftItems,
  getFeeGroups,
  msGetAllFeeTiers,
} from 'services/FinanceService';
import { getUserByIds } from 'services/UserService';
import { convertObjToCamelCase } from 'utils';
import { DEFAULT_ERROR, MAX_PER_PAGE } from '../utils/constants';
import DatadogHandler from '../utils/datadog';
import { getLevelByIds } from './SchoolConfigService';

const { FINANCE } = MS_PHASE_3_ENDPOINTS;

const fileName = 'src/services/FeeManagementService.js';

export const getFeeItems = async (filter, pagination) => {
  try {
    const { page = 1, perPage = MAX_PER_PAGE } = pagination || {};
    const feeDraftItems = await getFeeDraftItems(
      {
        filter,
      },
      {
        params: {
          page,
          perPage,
        },
      }
    );
    const levelIds = uniq(feeDraftItems.map(d => d?.fk_level));
    const programIds = uniq(feeDraftItems.map(d => d?.fk_program));
    const [programs, levels] = await Promise.all([
      getProgramByIds(programIds),
      getLevelByIds(levelIds),
    ]);
    const programMap = keyBy(programs, 'id');
    const levelMap = keyBy(levels, 'id');
    return feeDraftItems.map(d => ({
      ...convertObjToCamelCase(d, ['nationality:nationality_string']),
      program: {
        ID: d.fk_program,
        label: programMap[d.fk_program]?.label,
      },
      level: {
        ID: d.fk_level,
        label: levelMap[d.fk_level]?.label,
      },
    }));
  } catch (error) {
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllFeeDraft = async ({
  subqueries = {
    feeDraftItems: true,
    userByFkUser: true,
    userByFkApprover: true,
    feeTier: true,
    centre: true,
  },
  filter = {
    status: [
      {
        eq: 'draft',
      },
    ],
  },
  pagination,
} = {}) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      FINANCE.findAllFeeDraft,
      {
        filter,
      },
      {
        params: {
          page: pagination?.page || 1,
          perPage: pagination?.perPage || MAX_PER_PAGE,
          sort: pagination?.sort,
        },
      }
    );
    const data = resp?.data?.data || [];
    const centreIds = uniq(data.map(d => d?.fk_centre));
    const feeDraftIds = uniq(data.map(d => d?.id));
    const feeTierIds = uniq(data.map(d => d?.fk_fee_tier));
    const userIds = subqueries?.userByFkUser
      ? uniq(data.map(d => d?.fk_user))
      : [];
    const approverUserIds = subqueries?.userByFkApprover
      ? uniq(data.map(d => d?.fk_approver))
      : [];
    const [feeDraftItems, centres, allFeeTiers, users] = await Promise.all([
      subqueries?.feeDraftItems
        ? getFeeItems({
            draft_id: [
              {
                eq: feeDraftIds,
              },
            ],
          })
        : [],
      subqueries?.centre ? getCentreByIds(centreIds) : {},
      subqueries?.feeTier
        ? msGetAllFeeTiers({
            id: feeTierIds,
          })
        : [],
      getUserByIds([...userIds, ...approverUserIds]),
    ]);
    const centreMap = keyBy(centres?.data, 'id');
    const feeDraftMap = groupBy(feeDraftItems, 'fkDraft');
    const feeTierMap = keyBy(allFeeTiers?.data, 'ID');
    const userMap = keyBy(users, 'id');
    const feeGroupIds = uniq(allFeeTiers?.data?.map(d => d?.fkGroup));

    const feeGroups = subqueries?.feeTier
      ? await getFeeGroups({
          filter: {
            id: [
              {
                eq: feeGroupIds,
              },
            ],
          },
        })
      : [];
    const feeGroupMap = keyBy(feeGroups, 'id');

    const findAllFeeDraft =
      data.map(item => ({
        ...convertObjToCamelCase(item, ['status:status_string']),
        centre: subqueries?.centre
          ? {
              ID: item?.fk_centre,
              label: centreMap[item?.fk_centre]?.label,
            }
          : null,
        feeDraftItems: subqueries?.feeDraftItems
          ? {
              data: feeDraftMap[item?.id],
            }
          : null,
        feeTier: subqueries?.feeTier
          ? {
              ...convertObjToCamelCase(feeTierMap[item?.fk_fee_tier]),
              feeGroup: {
                groupCode:
                  feeGroupMap[feeTierMap[item?.fk_fee_tier]?.fkGroup]
                    ?.group_code,
              },
            }
          : null,
        userByFkUser: subqueries?.userByFkUser
          ? {
              firstname: userMap[item?.fk_user]?.firstname,
              lastname: userMap[item?.fk_user]?.lastname,
            }
          : null,
        userByFkApprover: subqueries?.userByFkApprover
          ? {
              firstname: userMap[item?.fk_approver]?.firstname || '',
              lastname: userMap[item?.fk_approver]?.lastname || '',
            }
          : null,
      })) || [];

    return {
      success: true,
      data: {
        findAllFeeDraft: {
          data: findAllFeeDraft,
          totalCount: resp?.data?.totalCount || 0,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllFeeDraft',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchUpcomingFeeTier = async reqData => {
  try {
    const { pagination, filter } = reqData;
    const resp = await financeService().post(
      FINANCE.findAllUpcomingFeeTier,
      {
        filter: {
          centre: filter?.fkCentre ? [{ eq: filter?.fkCentre }] : [],
          group: filter?.fkGroup ? [{ eq: filter?.fkGroup }] : [],
        },
      },
      {
        params: {
          page: pagination?.page || 1,
          perPage: pagination?.perPage,
        },
      }
    );
    const data = resp?.data?.data || [];
    const centreIds = uniq(data.map(d => d?.fk_centre));
    const feeGroupIds = uniq(data.map(d => d?.fk_group));
    const feeTierIds = uniq(data.map(d => d?.id));
    const [centres, feeGroups, approvedFees, feeDrafts] = await Promise.all([
      getCentreByIds(centreIds),
      getFeeGroups({
        filter: {
          id: [
            {
              eq: feeGroupIds,
            },
          ],
        },
      }),
      getApprovedFeeDraftByFeeTierIds(feeTierIds),
      msFetchAllFeeDraft({
        subqueries: {
          feeDraftItems: true,
        },
        filter: {
          status: [
            {
              eq: 'draft',
            },
          ],
          fee_tier_id: [
            {
              eq: feeTierIds,
            },
          ],
        },
      }),
    ]);

    const feeByFeeTier = await Promise.all(
      feeTierIds.map(feeTierId => getFeeByFeeTierIds([feeTierId]))
    );
    const centreMap = keyBy(centres?.data, 'id');
    const feeGroupMap = keyBy(feeGroups, 'id');
    const feesByFeeTierMap = feeByFeeTier.reduce((acc, curr) => {
      Object.assign(acc, curr.data);
      return acc;
    }, {});
    const fkUsers = Object.values(approvedFees).flatMap(item => [
      item.fk_user,
      item.fk_approver,
    ]);
    const users = await getUserByIds(fkUsers);
    const userMap = keyBy(users, 'id');
    const feeDraftByFeeTier = groupBy(
      feeDrafts?.data?.findAllFeeDraft?.data,
      'fkFeeTier'
    );

    return {
      data: {
        findAllUpcomingFeeTier: {
          data: data.map(d => ({
            ...convertObjToCamelCase(d),
            approvedFeeDraft: {
              data: [
                {
                  effectiveFrom: d?.effective_for_registration_from,
                  userByFkApprover: {
                    firstname:
                      userMap[approvedFees[d?.id]?.fk_approver]?.firstname,
                    lastname:
                      userMap[approvedFees[d?.id]?.fk_approver]?.lastname,
                  },
                  userByFkUser: {
                    firstname: userMap[approvedFees[d?.id]?.fk_user]?.firstname,
                    lastname: userMap[approvedFees[d?.id]?.fk_user]?.lastname,
                  },
                  updatedAt: approvedFees[d?.id]?.updated_at,
                },
              ],
            },
            centre: {
              ID: d?.fk_centre,
              label: centreMap[d?.fk_centre]?.label,
            },
            feeGroup: {
              groupCode: feeGroupMap[d?.fk_group]?.group_code,
            },
            fees: {
              data: (feesByFeeTierMap[d?.id] || []).map(f => ({
                ...convertObjToCamelCase(f, ['nationality:nationality_string']),
                level: {
                  label: f?.label?.split('-')[0]?.trim(),
                  ID: f?.fk_level,
                },
                program: {
                  label: f?.label?.split('-')[1]?.trim(),
                  ID: f?.fk_program,
                },
              })),
            },
            feeDrafts: {
              data: feeDraftByFeeTier[d?.id] || [],
              totalCount: feeDraftByFeeTier[d?.id]?.length || 0,
            },
            savedDraft: {
              data: feeDraftByFeeTier[d?.id] || [],
              totalCount: feeDraftByFeeTier[d?.id]?.length || 0,
            },
          })),
          totalCount: resp?.data?.totalCount || 0,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchUpcomingFeeTier',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchCurrentFeeTier = async () => {
  try {
    const resp = await financeService().post(
      FINANCE.findAllCurrentFeeTier,
      {},
      {
        params: {
          page: 1,
          perPage: 10,
        },
      }
    );
    const data = resp?.data?.data || [];
    const centreIds = uniq(data.map(d => d?.fk_centre));
    const feeGroupIds = uniq(data.map(d => d?.fk_group));
    const feeTierIds = uniq(data.map(d => d?.id));
    const [centres, feeGroups, approvedFees] = await Promise.all([
      getCentreByIds(centreIds),
      getFeeGroups({
        filter: {
          id: [
            {
              eq: feeGroupIds,
            },
          ],
        },
      }),
      getApprovedFeeDraftByFeeTierIds(feeTierIds),
    ]);
    const feeByFeeTier = await Promise.all(
      feeTierIds.map(feeTierId => getFeeByFeeTierIds([feeTierId]))
    );
    const fkUsers = Object.values(approvedFees).flatMap(item => [
      item.fk_user,
      item.fk_approver,
    ]);
    const users = await getUserByIds(fkUsers);
    const centreMap = keyBy(centres?.data, 'id');
    const feeGroupMap = keyBy(feeGroups, 'id');
    const feesByFeeTierMap = feeByFeeTier.reduce((acc, curr) => {
      Object.assign(acc, curr.data);
      return acc;
    }, {});
    const userMap = keyBy(users, 'id');

    return {
      data: {
        findAllCurrentFeeTier: {
          data: data.map(d => ({
            ...convertObjToCamelCase(d),
            approvedFeeDraft: {
              data: [
                {
                  effectiveFrom: d?.effective_for_registration_from,
                  userByFkApprover: {
                    firstname:
                      userMap[approvedFees[d?.id]?.fk_approver]?.firstname,
                    lastname:
                      userMap[approvedFees[d?.id]?.fk_approver]?.lastname,
                  },
                  userByFkUser: {
                    firstname: userMap[approvedFees[d?.id]?.fk_user]?.firstname,
                    lastname: userMap[approvedFees[d?.id]?.fk_user]?.lastname,
                  },
                  updatedAt: approvedFees[d?.id]?.updated_at,
                },
              ],
            },
            centre: {
              ID: d?.fk_centre,
              label: centreMap[d?.fk_centre]?.label,
            },
            feeGroup: {
              groupCode: feeGroupMap[d?.fk_group]?.group_code,
            },
            fees: {
              data: (feesByFeeTierMap[d?.id] || []).map(f => ({
                ...convertObjToCamelCase(f, ['nationality:nationality_string']),
                level: {
                  label: f?.label?.split('-')[0]?.trim(),
                  ID: f?.fk_level,
                },
                program: {
                  label: f?.label?.split('-')[1]?.trim(),
                  ID: f?.fk_program,
                },
              })),
            },
          })),
          totalCount: resp?.data?.totalCount || 0,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchCurrentFeeTier',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllFeeGroup = async () => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      FINANCE.findAllFeeGroup,
      {},
      {
        params: {
          page: 1,
          size: MAX_PER_PAGE,
        },
      }
    );
    return {
      data: {
        findAllFeeGroup: convertObjToCamelCase(resp?.data),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllFeeGroup',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSubmitFeeDraft = async reqData => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.submitFeeDraft, {
      id_draft: reqData.IDDraft,
      remark: reqData.remark,
    });

    return {
      data: {
        submitFeeDraft: resp?.data,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSubmitFeeDraft',
        fileName,
      },
      'error'
    );

    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msUpdateFeeDraftItems = async reqData => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    const { IDDraft, data: items } = reqData;

    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        updateFeeDraftItems: {
          success: true,
          draftId: IDDraft,
          itemsUpdated: items?.length || 0,
          message: `Successfully updated ${
            items?.length || 0
          } fee draft items (Mock MS Response)`,
        },
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 150));

    return mockResponse;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateFeeDraftItems',
        fileName,
      },
      'error'
    );

    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msAddNewFeeDraft = async reqData => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.newFeeDraft, {
      idFeeTier: reqData.IDFeeTier,
      items: reqData.data?.map(item => ({
        effective_from: item.effectiveFrom,
        level_id: item.fkLevel,
        nationality: item.nationality,
        program_id: item.fkProgram,
        amount: item?.amount,
      })),
    });

    return {
      data: {
        newFeeDraft: {
          ID: resp?.data?.id,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddNewFeeDraft',
        fileName,
      },
      'error'
    );

    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msRejectFeeChangeRequest = async reqData => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.rejectFeeChangeRequest, {
      id_draft: reqData.IDDraft,
      remark: reqData.remark,
    });

    return {
      data: {
        rejectFeeChangeRequest: resp?.data,
      },
      success: true,
    };
  } catch (error) {
    const errorMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRejectFeeChangeRequest',
        fileName,
      },
      'error'
    );

    throw new Error(errorMsg);
  }
};

export const msApproveFeeChangeRequest = async reqData => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.approveFeeChangeRequest, {
      id_draft: reqData.IDDraft,
      remark: reqData.remark,
    });

    return {
      data: {
        approveFeeChangeRequest: resp?.data?.success,
      },
      success: resp?.data?.success,
    };
  } catch (error) {
    const errorMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msApproveFeeChangeRequest',
        fileName,
      },
      'error'
    );

    throw new Error(errorMsg);
  }
};

export const msCancelFeeChangeRequest = async reqData => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.cancelFeeChangeRequest, {
      id_draft: reqData.IDDraft,
      remark: reqData.remark,
    });

    return {
      data: {
        cancelFeeChangeRequest: resp?.data?.success,
      },
      success: resp?.data?.success,
    };
  } catch (error) {
    const errorMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCancelFeeChangeRequest',
        fileName,
      },
      'error'
    );

    throw new Error(errorMsg);
  }
};
